import { useEffect } from "react";
import { Box, Typography, Chip, Avatar, styled } from "@mui/material";
import { SectionWrapper, TitleWrapper } from "../../../styles/sectionStyles";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import { StyleConstants } from "../../../styles/StyleConstants";
import data from "../../../data/Skills.json";

const ChartsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 24,
  marginTop: 16,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const ChartCard = styled(Box)(() => ({
  flex: 1,
  background: "#fff",
  borderRadius: 16,
  padding: "24px 16px 16px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
}));

const ChartLabel = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 15,
  textAlign: "center",
  marginBottom: 8,
  color: StyleConstants.BLACK_COLOR,
}));

const TechSection = styled(Box)(() => ({
  marginTop: 32,
  background: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
}));

const CategoryRow = styled(Box)(() => ({
  marginBottom: 20,
  "&:last-child": { marginBottom: 0 },
}));

const CategoryLabel = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  color: StyleConstants.HEADER,
  marginBottom: 10,
}));

const CustomAngleTick = (props: any) => {
  const { x, y, cx, cy, payload } = props;
  const dx = x - cx;
  const dy = y - cy;
  const pad = 8;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const offsetX = (dx / dist) * pad;
  const offsetY = (dy / dist) * pad;
  const anchor = Math.abs(dx) < 5 ? "middle" : dx > 0 ? "start" : "end";
  return (
    <text
      x={x + offsetX}
      y={y + offsetY}
      textAnchor={anchor}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
      fill={StyleConstants.BLACK_COLOR as string}
    >
      {payload.value}
    </text>
  );
};

export const SkillsContainer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SectionWrapper>
      <TitleWrapper>Skills</TitleWrapper>

      <ChartsRow>
        <ChartCard>
          <ChartLabel>Hard Skills</ChartLabel>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart
              cx="50%"
              cy="50%"
              outerRadius="55%"
              data={data.hardSkills}
            >
              <PolarGrid stroke="#dce6ef" />
              <PolarAngleAxis dataKey="subject" tick={<CustomAngleTick />} />
              <Radar
                name="Hard Skills"
                dataKey="competency"
                stroke={StyleConstants.HEADER as string}
                fill={StyleConstants.HEADER as string}
                fillOpacity={0.25}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartLabel>Soft Skills</ChartLabel>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart
              cx="50%"
              cy="50%"
              outerRadius="55%"
              data={data.softSkills}
            >
              <PolarGrid stroke="#dce6ef" />
              <PolarAngleAxis dataKey="subject" tick={<CustomAngleTick />} />
              <Radar
                name="Soft Skills"
                dataKey="competency"
                stroke="#e8845f"
                fill="#e8845f"
                fillOpacity={0.25}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsRow>

      <TechSection>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 17,
            marginBottom: 2,
            color: StyleConstants.BLACK_COLOR,
          }}
        >
          Technical Stack
        </Typography>
        {data.technicalSkills.map((category) => (
          <CategoryRow key={category.category}>
            <CategoryLabel>{category.category}</CategoryLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {category.skills.map((skill) => (
                <Chip
                  key={skill.name}
                  avatar={
                    <Avatar
                      variant="square"
                      src={skill.logo}
                      sx={{ width: 20, height: 20 }}
                    />
                  }
                  label={skill.name}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: "8px",
                    backgroundColor: "#f4f7fa",
                    "&:hover": {
                      backgroundColor: StyleConstants.NAVIGATION_BG,
                    },
                  }}
                />
              ))}
            </Box>
          </CategoryRow>
        ))}
      </TechSection>
    </SectionWrapper>
  );
};
